import { Fade, Link, Typography } from "@mui/material";
import { useErrorHandler } from "@smartrr/shared/components/ErrorBoundary";
import { FailedBill } from "@smartrr/shared/entities/Billing";
import { OrgUtils } from "@smartrr/shared/entities/Organization";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { getPurchStFutureScheduledDeliveriesWithNonActive } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { rgba } from "polished";
import React, { useCallback } from "react";
import { Trans } from "react-i18next";

import { useToggleModal } from "@customer-app/state/actionCreators/modal";

import useFailedPaymentBannerCopy from "./useFailedPaymentBannerCopy";
import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";
import { useOrganization } from "../../../utils";
import { AquariusBanner } from "../../AquariusBanner";
import { useBillStore } from "../../store/BillsStore";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

const FailedPaymentBannerInner = ({
  failedBill,
  onUpdatePayment,
  purchaseState,
}: {
  failedBill: FailedBill;
  onUpdatePayment: () => void;
  purchaseState: IPurchaseState;
}) => {
  const theme = useSmartrrTheme();
  const bannerCopy = useFailedPaymentBannerCopy(failedBill, purchaseState);
  return (
    <Fade in>
      <div data-testid="failed-payment-banner">
        <AquariusBanner state="error" useIcon>
          <Trans
            className="failed-payment-banner-copy"
            defaults={bannerCopy.noticeCopy}
            components={[
              <Typography
                data-testid="failed-payment-banner-retry-count"
                component={"span"}
                variant="body2"
                key={0}
              />,
              <Link
                data-testid="failed-payment-banner-update-payment"
                component="span"
                underline="always"
                onClick={onUpdatePayment}
                sx={{
                  color: theme.modernBodyFontColor,
                  cursor: "pointer",
                  textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                }}
                key={1}
              />,
              <Link
                data-testid="failed-payment-banner-contact"
                component="a"
                underline="always"
                href={bannerCopy.contactHref}
                sx={{
                  color: theme.modernBodyFontColor,
                  cursor: "pointer",
                  textDecorationColor: rgba(theme.modernBodyFontColor!, 0.4),
                }}
                key={2}
              />,
            ]}
          />
          <br />
          <br />
          <Typography variant="body1" fontStyle="italic">
            <Trans
              defaults={bannerCopy.noteCopy}
              components={[<Typography component={"span"} variant="body2" key={0} />]}
            />
          </Typography>
        </AquariusBanner>
      </div>
    </Fade>
  );
};

export const FailedPaymentBanner = ({ purchaseState }: { purchaseState: IPurchaseState }) => {
  const { failedBills } = useBillStore();

  const errorHandler = useErrorHandler();
  const organization = useOrganization();
  const toggleModal = useToggleModal();

  const onUpdatePayment = useCallback(() => {
    if (purchaseState) {
      const scheduledDeliveries = getPurchStFutureScheduledDeliveriesWithNonActive(
        purchaseState,
        OrgUtils.getBillingSchedule(organization),
        1
      );
      if (scheduledDeliveries?.length) {
        const delivery = scheduledDeliveries[0]!;

        toggleModal("MANAGE_SUBSCRIPTIONS", {
          deliveryId: getDeliveryId(delivery),
          purchasablesForAdding: [],
          generatePurchasables: true,
          initialView: "Edit payment",
        });
      }
    } else {
      errorHandler(new Error("No upcoming purchase state"));
    }
  }, []);

  if (!failedBills.length) {
    return <React.Fragment />;
  }

  return (
    <FailedPaymentBannerInner
      failedBill={failedBills[0]}
      onUpdatePayment={onUpdatePayment}
      purchaseState={purchaseState}
    />
  );
};
